import React, { useEffect, useState } from "react";
import Navbar from "../../components/layout/navbar";
import useRequest from "../../components/hooks/use-request";
import Button from "../../components/button";
import { Plus } from "lucide-react";
import AddOrderDialog from "../../components/orders";
import OrderTable from "./order-table";
import { I_Customer } from "../customers/types";
import MetricsCard from "./metrics-card";
import { I_ProductData } from "../inventory/types";
import Pagination from "../../components/pagination";

interface ProfileData {
  username: string;
  email: string;
  phone: string;
  address: string;
  lastLogin: string;
}

interface MetricsData {
  totalOrders: number;
  totalOngoingOrders: number;
  totalPaidOrders: number;
  totalUnpaidOrders: number;
}

const Orders = () => {
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [orders, setOrders] = useState<[]>([]);
  const [metrics, setMetrics] = useState<MetricsData | null>(null);
  const [customer, setCustomer] = useState<I_Customer[]>([]);
  const [ordersModal, setOrdersModal] = useState(false);
  const [product, setProduct] = useState<I_ProductData[]>([]);
  const params = new URLSearchParams(new URL(window.location.href).search);
  const [currentPage, setCurrentPage] = useState(
    Number(params.get("page") || 1)
  );
  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalDocs, setTotalDocs] = useState<number>(0);
  const [currentPageItems, setCurrentPageItems] = useState<number>(0);
  const itemsPerPage = 10;
  const userToken = localStorage.getItem("token");
  const { makeRequest: getProfile } = useRequest("/users/profile", "GET", {
    Authorization: `Bearer ${userToken}`,
  });
  const { makeRequest: getOrders } = useRequest("/orders", "GET", {
    Authorization: `Bearer ${userToken}`,
  });
  const { makeRequest: getCustomers } = useRequest("/customers", "GET", {
    Authorization: `Bearer ${userToken}`,
  });

  const { makeRequest: getMetrics } = useRequest("/orders/metrics", "GET", {
    Authorization: `Bearer ${userToken}`,
  });

  const { makeRequest: getProducts } = useRequest("/inventory", "GET", {
    Authorization: `Bearer ${userToken}`,
  });

  useEffect(() => {
    const fetchProfile = async () => {
      const [response] = await getProfile();
      if (response.status) {
        setProfile(response.data?.user);
      }
    };
    fetchProfile();
  }, []);

  function updateUrlParams(params: { [key: string]: string | number | null }) {
    const url = new URL(window.location.href);

    Object.keys(params).forEach((key) => {
      if (params[key]) {
        url.searchParams.set(key, params[key]!.toString());
      } else {
        url.searchParams.delete(key);
      }
    });



    window.history.pushState({}, "", url.toString());
  }

  useEffect(() => {
    updateUrlParams({
      page: currentPage !== 1 ? currentPage : null,
    });
    fetchOrders();
  }, [currentPage]);

  async function fetchOrders() {
    const page = currentPage;
    const limit = itemsPerPage;

    const [response] = await getOrders(undefined, {
      limit,
      page,
    });

    if (response.status && response.data?.orders) {
      setOrders(response.data.orders.docs || []);
      setTotalPages(response.data.orders.totalPages || 1);
      setTotalDocs(response.data.orders.totalDocs || 0);
      setCurrentPageItems(response.data.orders.docs?.length || 0);
    }
  }

  useEffect(() => {
    const fetchCustomers = async () => {
      const [response] = await getCustomers();
      if (response.status) {
        setCustomer(response.data?.customers?.docs);
      }
    };
    fetchCustomers();
  }, []);

  function closeOrderModal() {
    setOrdersModal(false);
  }

  function openOrderModal() {
    setOrdersModal(true);
  }
  
  useEffect(() => {
    const fetchProducts = async () => {
      const [response] = await getProducts();
      if (response.status) {
        setProduct(response.data?.inventory?.docs);
      }
    };
    fetchProducts();
  }, []);

  useEffect(() => {
    const fetchMetrics = async () => {
      const [response] = await getMetrics();
      if (response.status) {
        setMetrics(response.data);
      }
    };
    fetchMetrics();
  }, []);

  async function handlePageChange(page: number) {
    setCurrentPage(page);
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Navbar profile={profile as ProfileData} />
      <main className="flex-1 container mx-auto py-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6">
          <div className="mb-4 sm:mb-0">
            <h1 className="text-[30px] font-extrabold text-black">
              Sales Order
            </h1>
            <p className="text-[#676767] text-[18px] font-normal">
              Manage all customer orders in one place
            </p>
          </div>
          <div className="flex gap-2">
            <button className="rounded-full border p-2">Export</button>

            <Button
              size="lg"
              variant="primary"
              type="button"
              className="font-normal md:mt-0 mt-6"
              onClick={openOrderModal}
            >
              <Plus />
              Create Sales Order
            </Button>
          </div>
        </div>
        {metrics && <MetricsCard metrics={metrics} />}

        <OrderTable data={orders} />

        {totalPages > 1 && (
          <div className="mt-4">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              totalDocs={totalDocs}
              totalItems={currentPage === totalPages ? totalDocs : currentPageItems}
            />
          </div>
        )}
      </main>
      <AddOrderDialog
        isVisible={ordersModal}
        onClose={closeOrderModal}
        customers={customer}
        product={product}
      />
    </div>
  );
};

export default Orders;